import React, { useState, useCallback, useMemo, useRef } from 'react';
import { useGetList, useNotify } from 'react-admin';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Stepper from '@mui/material/Stepper';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';

import type { FacetGroup } from '@src/facets';

import { WIZARD_STEPS, INITIAL_WIZARD_STATE } from './types';
import type { WizardState, CatalogTemplateRow } from './types';
import { fetchCatalogItemsForTemplate } from './fetchCatalogItemsForTemplate';
import { generateTemplateCsv, detectAmbiguousPricing } from './generateTemplateCsv';
import ScopeStep from './ScopeStep';
import VitalElementsStep from './VitalElementsStep';
import PricingStep from './PricingStep';
import ReasonCodeStep from './ReasonCodeStep';
import ReviewStep from './ReviewStep';

interface CreateOverrideTemplateDialogProps {
  open: boolean;
  onClose: () => void;
}

const REVIEW_STEP = WIZARD_STEPS.length - 1;

const CreateOverrideTemplateDialog = ({ open, onClose }: CreateOverrideTemplateDialogProps) => {
  const notify = useNotify();
  const [state, setState] = useState<WizardState>(INITIAL_WIZARD_STATE);
  const [activeStep, setActiveStep] = useState(0);
  const [catalogRows, setCatalogRows] = useState<CatalogTemplateRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const { data: facetGroups = [] } = useGetList<FacetGroup>('facets');

  const updateState = useCallback((patch: Partial<WizardState>) => {
    setState(prev => ({ ...prev, ...patch }));
  }, []);

  const handleClose = useCallback(() => {
    setState(INITIAL_WIZARD_STATE);
    setActiveStep(0);
    setCatalogRows([]);
    setIsLoading(false);
    onClose();
  }, [onClose]);

  const isStepValid = useMemo(() => {
    switch (activeStep) {
      case 0: // Scope
        if (state.scopeMode === 'departments') {
          return state.selectedDepartments.length > 0 || state.selectedCategories.length > 0;
        }
        if (state.scopeMode === 'parentSkus') {
          return state.parentSkuInput.trim().length > 0;
        }
        return true;
      case 1: { // Vital Elements
        if (state.selectedVitalElements.length === 0) return false;
        if (isLoading) return false;
        if (catalogRows.length === 0) return true; // empty scope — Review handles this
        return detectAmbiguousPricing(catalogRows, state.selectedVitalElements).length === 0;
      }
      case 2: // Pricing
        if (state.pricingMode === 'percentOff') {
          return state.percentOff > 0 && state.percentOff <= 100;
        }
        return true;
      case 3: // Reason Code
        return state.overrideType.trim().length > 0 && state.overrideCodeSuffix.trim().length > 0;
      default:
        return true;
    }
  }, [activeStep, state, isLoading, catalogRows]);

  const fetchGeneration = useRef(0);

  const fetchCatalogData = useCallback(async () => {
    const generation = ++fetchGeneration.current;
    setIsLoading(true);
    try {
      const rows = await fetchCatalogItemsForTemplate(state);
      if (generation !== fetchGeneration.current) return; // stale fetch
      setCatalogRows(rows);
    } catch (error) {
      if (generation !== fetchGeneration.current) return; // stale fetch
      console.error('CreateOverrideTemplateDialog: failed to fetch catalog items', {
        error: error instanceof Error ? error.message : error,
      });
      const message = error instanceof Error ? error.message : 'Failed to load catalog items';
      notify(`Error: ${message}`, { type: 'error' });
    } finally {
      if (generation === fetchGeneration.current) {
        setIsLoading(false);
      }
    }
  }, [state, notify]);

  const handleNext = useCallback(async () => {
    if (activeStep === 0) {
      setActiveStep(prev => prev + 1);
      await fetchCatalogData();
    } else {
      setActiveStep(prev => prev + 1);
    }
  }, [activeStep, fetchCatalogData]);

  const handleBack = useCallback(() => {
    if (activeStep === 1) {
      setCatalogRows([]); // scope may change — invalidate catalog data
    }
    setActiveStep(prev => prev - 1);
  }, [activeStep]);

  const generateFilename = useCallback((): string => {
    const date = new Date().toISOString().slice(0, 10);
    const parts: string[] = ['override_template'];

    // Add scope descriptor
    if (state.scopeMode === 'departments') {
      // Handle departments
      if (state.selectedDepartments.length === 1) {
        parts.push(state.selectedDepartments[0].toLowerCase().replace(/\s+/g, '_'));
      } else if (state.selectedDepartments.length > 1) {
        parts.push(`${state.selectedDepartments.length}depts`);
      }

      // Handle categories
      if (state.selectedCategories.length === 1) {
        parts.push(state.selectedCategories[0].toLowerCase().replace(/\s+/g, '_'));
      } else if (state.selectedCategories.length > 1) {
        parts.push(`${state.selectedCategories.length}cats`);
      }
    } else if (state.scopeMode === 'parentSkus') {
      const skuCount = state.parentSkuInput.split(/[,\n]+/).filter(s => s.trim()).length;
      parts.push(skuCount === 1 ? 'single_sku' : `${skuCount}skus`);
    }

    // Add reason code if provided
    if (state.reasonCode.trim()) {
      const sanitizedCode = state.reasonCode.trim().toLowerCase().replace(/\s+/g, '_');
      parts.push(sanitizedCode);
    }

    // Add date
    parts.push(date);

    return `${parts.join('_')}.csv`;
  }, [state]);

  const handleDownload = useCallback(() => {
    const csv = generateTemplateCsv(catalogRows, state);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = generateFilename();
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    notify('Template downloaded', { type: 'success' });
    handleClose();
  }, [catalogRows, state, notify, handleClose, generateFilename]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth={false} fullWidth sx={{ '& .MuiDialog-paper': { maxWidth: 975 } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Create Override Template
        <IconButton size="small" onClick={handleClose} aria-label="close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Stepper activeStep={activeStep} sx={{ pt: 1, pb: 3 }}>
          {WIZARD_STEPS.map(label => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {activeStep === 0 && (
          <ScopeStep state={state} updateState={updateState} facetGroups={facetGroups} />
        )}
        {activeStep === 1 && (
          <VitalElementsStep
            state={state}
            updateState={updateState}
            catalogRows={catalogRows}
            isLoading={isLoading}
          />
        )}
        {activeStep === 2 && (
          <PricingStep state={state} updateState={updateState} />
        )}
        {activeStep === 3 && (
          <ReasonCodeStep state={state} updateState={updateState} />
        )}
        {activeStep === REVIEW_STEP && (
          <ReviewStep state={state} catalogRows={catalogRows} isLoading={isLoading} />
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={handleClose} sx={{ mr: 'auto' }}>Cancel</Button>
        {activeStep > 0 && (
          <Button onClick={handleBack}>Back</Button>
        )}
        {activeStep < REVIEW_STEP ? (
          <Button variant="contained" onClick={handleNext} disabled={!isStepValid}>
            Next
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleDownload}
            disabled={isLoading || catalogRows.length === 0}
            startIcon={<DownloadIcon />}
          >
            Download Template
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CreateOverrideTemplateDialog;
